/**
 *
 * ShellHeader
 *
 */
import Button from '@react/react-spectrum/Button';
import classNames from 'classnames';
import Icon from '@react/react-spectrum/Icon';
import Label from '@react/react-spectrum/Label';
import Heading from '@react/react-spectrum/Heading';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { HeaderActions } from '../HeaderActions';
import { InternalUseOnlyBadge } from './InternalUseOnlyBadge';
import Visibility from '@spectrum-icons/workflow/Visibility';

import './ShellHeader.scss';
import styles from './ShellHeader.module.scss';
import { ThemeContext } from 'context/themeContext';
import { ThemeContextType } from 'types/theme';
import { useDispatch, useSelector } from 'react-redux';
import AskAiIcon from 'app/assets/svgIcons/AskAiIcon';
import { layoutActions, useLayoutSlice } from 'app/pages/Layout/slice';
import { selectCanAccessAiAssistant } from 'app/pages/Layout/slice/selectors';
import SearchBar from 'app/components/Fields/SearchField';
import { selectFullScreenMode } from 'app/pages/Home/slice/selectors';
import { Badge, Flex } from '@adobe/react-spectrum';

const CoachMark = React.lazy(() => import('@react/react-spectrum/CoachMark'));

function HeroImage({ icon, theme, title }) {
  let className;
  if (theme === 'lightest') {
    className = 'light-border';
  } else if (theme === 'darkest') {
    className = 'dark-border';
  }
  return (
    <Icon size="XXL">
      <img className={className} alt={title} src={icon} />
    </Icon>
  );
}

export const ShellHeader = props => {
  const { solutionIcon, solutionTitle } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  useLayoutSlice();
  const isFullScreenMode = useSelector(selectFullScreenMode);
  const canAccessAiAssistant = useSelector(selectCanAccessAiAssistant);
  const onSolutionClick = () => {
    navigate('/');
  };
  const { theme, sideNavEnabled } = React.useContext(
    ThemeContext
  ) as ThemeContextType;
  const getEnvironmentLabel = environment => {
    if (props.devMode) {
      return 'Dev Mode';
    }

    switch (environment) {
      case 'dev':
        return 'Dev';
      case 'qa':
        return 'QA';
      case 'stage':
        return 'Staging';
      case 'test':
        return 'Test';
    }
  };

  const renderImpersonationLabel = () => {
    const { isImpersonating, userFirstName, userLastName } = props;
    if (!isImpersonating) {
      return;
    }
    return (
      <div className="spectrum-Shell-coloredLabel-container impersonation-label">
        <Label className="spectrum-Shell-coloredLabel">
          <div className="spectrum-Shell-impersonationLabel">
            <Visibility size="S" />
            <span className="spectrum-Shell-impersonationLabel-name">
              {userFirstName} {userLastName}
            </span>
          </div>
        </Label>
      </div>
    );
  };

  const renderEnvLabel = () => {
    const { accessOverridesEnabled, customEnvLabel, environment, feedback } =
      props;
    const envLabel = getEnvironmentLabel(environment);
    return (
      <div
        className={classNames('spectrum-Shell-coloredLabel-container', {
          hide: !feedback || !feedback.enabled
        })}
      >
        {envLabel && (
          <Label
            className={classNames('spectrum-Shell-coloredLabel', {
              smallMargin: customEnvLabel?.length
            })}
            variant="orange"
          >
            {envLabel}
          </Label>
        )}
        {(customEnvLabel || []).slice(0, 2).map(
          (label, index) =>
            label && (
              <Label
                className={classNames('spectrum-Shell-coloredLabel', {
                  smallMargin: index < customEnvLabel.length - 1
                })}
                key={index}
                variant="green"
              >
                {label}
              </Label>
            )
        )}
        {accessOverridesEnabled && (
          <Label className="spectrum-Shell-coloredLabel" variant="red">
            Overrides on
          </Label>
        )}
      </div>
    );
  };
  const leftContainerElementOrderSwitcher = () => {
    const { feedback, showMoreActionsButton } = props;
    // Show environment labels in header when they are not collapsed in More Actions button
    // or when feedback button isn't present (when feedback button is present,
    // they go into the second bar of the More Actions).
    const showEnvLabel = !showMoreActionsButton || !(feedback || {}).enabled;
    const showImpersonationLabel =
      !showMoreActionsButton || !(feedback || {}).enabled;
    return (
      <>
        {showEnvLabel ? renderEnvLabel() : null}
        {showImpersonationLabel ? renderImpersonationLabel() : null}
      </>
    );
  };

  const leftContainer = (inSearchPopover = false) => {
    return (
      <div className="spectrum-Shell-leftContainer">
        {/* {sideNavEnabled && (
          <SideNavMenuButton
            sideNavOpen={sideNavOpen}
            sideNavOpenByHover={sideNavOpenByHover}
            toggleSideNav={toggleSideNav}
          />
        )} */}
        <Flex gap={'size-200'} alignItems="center">
          <Button
            aria-labelledby="spectrum-Shell-logoLabel"
            className="spectrum-Shell-logoIcon"
            data-omega-element="hero button"
            onClick={onSolutionClick}
            onKeyUp={onSolutionClick}
            variant="tool"
          >
            <HeroImage
              icon={solutionIcon}
              theme={theme}
              title={solutionTitle}
            />
          </Button>
          <Flex gap={'size-200'} alignItems="center">
            <h2
              style={{
                color: 'var(--spectrum-global-color-gray-900)'
              }}
            >
              Roadmap
            </h2>
            <InternalUseOnlyBadge />
          </Flex>
        </Flex>
        {!inSearchPopover && leftContainerElementOrderSwitcher()}
      </div>
    );
  };

  const {
    appContainer,
    coachMark,
    feedback,
    modal,
    onSignOutClick,
    workspaces
  } = props;

  const showCoachMark = coachMark && coachMark.enabled && !coachMark.hidden;
  const showUnderlay = showCoachMark || (!appContainer && modal);
  return (
    <header
      aria-hidden={showCoachMark || modal}
      className={classNames('spectrum-Shell', `spectrum--${theme}`, {
        'has-appContainer': appContainer,
        'modal-open': modal,
        // extra class needed for the spectrum's light stylesheet
        'spectrum--light': theme === 'lightest',
        'spectrum--dark': theme === 'darkest'
      })}
      style={{ display: isFullScreenMode ? 'none' : 'inherit' }}
    >
      <div
        className={classNames('spectrum-Underlay', {
          'is-open': showUnderlay
        })}
        style={{ position: 'absolute' }}
      ></div>
      <div
        className={`spectrum-Shell-headingContainer ${
          workspaces?.length > 0 ? 'workspacesPresent' : 'noWorkspaces'
        }`}
        style={{ boxShadow: '0px 1px 6px 0px rgba(0, 0, 0, 0.12)' }}
      >
        {showCoachMark && (
          <React.Suspense fallback={null}>
            <CoachMark selector={coachMark.element} title="" />
          </React.Suspense>
        )}
        {leftContainer()}
        <div className="custom-search-bar">
          <div className="custom-search-bar-search">
            <SearchBar />
          </div>
          {canAccessAiAssistant && (
            <Button
              className={styles.askAI}
              data-omega-element="ask ai assistant"
              type="button"
              variant="secondary"
              quiet
              icon={<AskAiIcon width={20} height={20} />}
              label="Ask AI"
              onClick={() => dispatch(layoutActions.toggleAiAssistant())}
            />
          )}
        </div>
        <div className="spectrum-Shell-rightContainer">
          <HeaderActions feedback={feedback} onSignOutClick={onSignOutClick} />
        </div>
      </div>
      {props.children}
    </header>
  );
};
